<svelte:options runes={false} />
<script>
  // Trades tab (U14b): the strategy's blotter (orders + fills), newest-first. Reuses the shared TradesTable
  // (the same presentation the Backtester + PerformancePanel use). The series is seeded from the membership-
  // gated performance read (U5) — `result.trades` — and refreshes LIVE through the host's authoritative refetch
  // on each order/NAV SSE hint (KTD7), so a new fill appears within the refetch cadence for a running strategy.
  //
  // TASK-20260825-twap-parent-rows-illegible-in-trades-tab: a TWAP parent's ledger row is a write-ahead intent
  // for the WHOLE parent and stays `Placing` by design until the parent settles, so it rendered as a bare
  // "Placing" trade indistinguishable from a hung order. The row's `clientOrderId` IS the twp… parent id, so we
  // join it client-side to the parent summaries this strategy already owns — no server join, no new endpoint.
  import TradesTable from '../../shared/TradesTable.svelte';
  import api from '../../../lib/api.js';
  import { selectedOrgId } from '../../../lib/orgContext.js';
  import { createScopedLoader } from '../../../lib/scopedLoader.ts';
  import { decorateTrades, twapParentsById, hasTwapParentRows } from '../../../lib/tradesTwapParents.js';

  export let strategy = null;
  export let result = null;
  export let loading = false;

  /** The server's hard ceiling (TwapParentQuery.MaxPageSize clamps to this). */
  const PARENTS_PAGE_SIZE = 200;

  let parentsById = new Map();

  $: strategyId = strategy?.strategyId ?? null; // the AutoTradeRequests id = the parents' RequestId
  $: rawTrades = result?.trades ?? [];

  // Only fetch when a twp… row is actually on screen: a deployment that never ran TWAP must not pay for a
  // request it cannot use. The prefix check is local and free (the id is self-identifying). The predicate is
  // the module's, NOT an inline startsWith — the gate and the decoration must never disagree about what a
  // parent id is.
  $: hasTwapRows = hasTwapParentRows(rawTrades);

  // Tenant-scoped through the ONE predicate (TASK-20260818 F1/F8): it skips entirely while the org is
  // unresolved — a scope-less read is a guaranteed 400 (ADR-0023) and the startup window is normal, not a
  // failure — and drops any response, success OR failure, that lands after the org or strategy moved on.
  // A failure leaves `parentsById` empty, which degrades to exactly today's rendering rather than blanking rows.
  const loadParents = createScopedLoader({
    getOrgId: () => $selectedOrgId,
    getScope: () => [strategyId],
    load: async (orgId) => {
      const page = await api.getTwapParents({ requestId: strategyId, pageSize: PARENTS_PAGE_SIZE }, orgId);
      const items = Array.isArray(page?.items) ? page.items : [];
      // The server caps a page at 200 and the trades list is UNCAPPED, so a long-running TWAP deployment can
      // own more parents than one page carries. Older ones then simply do not decorate — acceptable (AC 9
      // degradation), but it must not be SILENT: an undecorated row is exactly the ambiguity this tab exists
      // to remove, so say so rather than letting the join look complete.
      if (typeof page?.total === 'number' && page.total > items.length) {
        console.warn(
          `TradesTab: ${page.total} TWAP parents for this strategy but only the newest ${items.length} were read; ` +
          'older parent rows will render undecorated.');
      }
      return items;
    },
    apply: (items) => { parentsById = twapParentsById(items); },
    onError: () => { parentsById = new Map(); },
  });

  // Re-read on a new trades PAYLOAD (identity, not length), on an org change, or on a strategy change.
  //
  // Identity is the load-bearing part. The host refetches the performance read on every order SSE hint and
  // `strategyPerformance.set(perf)` publishes a FRESH object each time, so `rawTrades` is a new array — but a
  // parent advancing 6/9 → 7/9 cycles creates NO new ledger row, so the array's LENGTH never changes. Keying
  // off length would pin the cycle count at whatever it was on first render, which is the exact staleness this
  // tab exists to fix. This mirrors Orders.svelte, which keys off `$orders !== lastOrdersRef` for the same reason.
  let lastTradesRef = null;
  let lastOrgId = null;
  let lastStrategyId = null;
  $: if (strategyId && hasTwapRows
        && (rawTrades !== lastTradesRef || $selectedOrgId !== lastOrgId || strategyId !== lastStrategyId)) {
    lastTradesRef = rawTrades;
    lastOrgId = $selectedOrgId;
    lastStrategyId = strategyId;
    void refreshParents();
  }

  // Serialize the fetches (the TwapTab.svelte pattern). createScopedLoader drops a response only when the
  // SCOPE moved on; two calls at the SAME (org, strategy) both commit, in whatever order the network returns
  // them. Cycles fire on a slice interval and each child fill triggers a refetch, so overlap is realistic —
  // and because the thing being displayed is a MONOTONIC counter, an out-of-order landing renders the count
  // going BACKWARDS (7/9 after 8/9). A stale count is bad; a reversing one destroys trust in the number.
  // One in flight at a time, with a single coalesced follow-up.
  let inFlight = false;
  let queued = false;
  async function refreshParents() {
    if (inFlight) { queued = true; return; }
    inFlight = true;
    try {
      await loadParents();
    } finally {
      inFlight = false;
      if (queued) { queued = false; void refreshParents(); }
    }
  }

  $: trades = decorateTrades(rawTrades, parentsById);
</script>

<div class="at-trades-tab">
  {#if loading && !result}
    <div class="at-skeleton-rows"><div class="at-skeleton-row"></div></div>
  {:else if trades.length}
    <TradesTable {trades} />
  {:else}
    <div class="empty-state">
      <p class="empty-state-description">No fills yet for this strategy. Trades will appear here as the strategy executes.</p>
    </div>
  {/if}
</div>

<style>
  @import '../../Backtesting/Backtesting.css';
</style>
